import React, { useState, useContext, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LanguageContext } from "../../context/LanguageContext";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import ReCAPTCHA from "react-google-recaptcha";

const RegisterForm = () => {
  const { t } = useContext(LanguageContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const IS_PRODUCTION = process.env.NODE_ENV === "production";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "club",
    password: "",
    address: "",
    contact_person: "",
    contact_email: "",
    phone: "",
    iban: "",
    bic: "",
    second_contact_person: "",
    second_contact_phone: "",
    second_contact_email: "",
    agree: false,
    agree_dataprotection: false,
    agree_billing_terms: false,
    location_of_signing: "",
    date_of_signing: "",
    signed_by: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const token = searchParams.get("token");

    const validateToken = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/validateInviteToken?token=${token}`);
        const data = await res.json();

        if (res.ok) {
          const { email, name } = data;
          setFormData((prev) => ({
            ...prev,
            email,
            name: name || "",
            role: "club",
          }));
          setIsPrefilled(true);
        } else {
          toast.error(data.message || "Invalid or expired registration link", { position: "top-right" });
          navigate("/error"); // or some info page
        }
      } catch (err) {
        console.error("Token validation error:", err.message);
        toast.error("Something went wrong", { position: "top-right" });
        navigate("/error");
      }
    };

    if (token) {
      validateToken();
    }
  }, [searchParams]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = t("name_required");
    if (!formData.email.trim()) newErrors.email = t("email_required");
    if (!formData.password || formData.password.length < 6)
      newErrors.password = t("password_short");

    if (formData.role === "club") {
      if (!formData.address.trim()) newErrors.address = t("address_required");
      if (!formData.contact_person.trim()) newErrors.contact_person = t("contact_person_required");
      if (!formData.contact_email.trim() || !/^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.contact_email))
        newErrors.contact_email = t("contact_email_invalid");
      if (!formData.phone.trim()) newErrors.phone = t("phone_required");
      if (!formData.iban.trim()) newErrors.iban = t("iban_required");
      if (!formData.bic.trim()) newErrors.bic = t("bic_required");
      if (!formData.agree_dataprotection) newErrors.agree_dataprotection = t("agree_dataprotection_required");
      if (!formData.agree_billing_terms) newErrors.agree_billing_terms = t("agree_billing_terms_required");
      if (!formData.location_of_signing.trim()) newErrors.location_of_signing = t("location_of_signing_required");
      if (!formData.date_of_signing.trim()) newErrors.date_of_signing = t("date_of_signing_required");
      if (!formData.signed_by.trim()) newErrors.signed_by = t("signed_by_required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

     if (IS_PRODUCTION && !captchaToken) {
      setCaptchaError("Please complete the CAPTCHA.");
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(t("Registration successful!"), { position: "top-right" });
        navigate("/login");
      } else {
        toast.error(t(data.message || "Registration failed"), { position: "top-right" });
      }
    } catch (err) {
      console.error("Register error:", err);
      toast.error(t("Something went wrong"), { position: "top-right" });
    }
  };

  const renderInput = (name, label, type = "text", placeholder = "", required = false, readOnly = false) => {
    const isOptionalAlreadyInLabel = label.toLowerCase().includes("optional");

    return (
      <div className="flex flex-col">
        <label htmlFor={name} className="text-sm font-medium text-gray-700 mb-1">
          {label} {!required && !isOptionalAlreadyInLabel && <span className="text-gray-400">{t("optional")}</span>}
          {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          readOnly={readOnly}
          placeholder={placeholder || label.replace(/\s*\(optional\)/i, '').trim()}
          className={`p-2 border rounded-md focus:outline-none focus:ring-2 transition
            ${errors[name] ? "border-red-500" : "border-gray-300"}
            ${readOnly ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}
          `}
        />
        {errors[name] && <p className="text-red-500 text-xs mt-1">{errors[name]}</p>}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto mt-10 px-6 py-8 bg-white rounded-xl shadow-md">
      <h2 className="text-3xl font-semibold mb-4 text-center">{t("register_title")}</h2>
      <p className="text-sm text-gray-500 mb-6 text-center">
        Fields marked with <span className="text-red-500">*</span> are required.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderInput("name", t("name"), "text", "", true, isPrefilled)}
          {renderInput("email", t("email"), "email", "", true, isPrefilled)}
          
          {renderInput("password", t("password"), "password", "", true)}
        </div>

        {formData.role === "club" && (
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-md space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">{t("club_information")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderInput("address", t("address"), "text", "", true)}
              {renderInput("contact_person", t("contact_person"), "text", "", true)}
              {renderInput("contact_email", t("contact_email"), "email", "", true)}
              {renderInput("phone", t("phone"), "text", "", true)}
              {renderInput("iban", t("iban"), "text", "", true)}
              {renderInput("bic", t("bic"), "text", "", true)}
              {renderInput("second_contact_person", t("second_contact_person"))}
              {renderInput("second_contact_phone", t("second_contact_phone"))}
              {renderInput("second_contact_email", t("second_contact_email"), "email")}
            </div>

            <div className="flex flex-col space-y-2">
              <label className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  name="agree_dataprotection"
                  checked={formData.agree_dataprotection}
                  onChange={handleChange}
                  className="mt-1"
                />
                <span>{t("agree_dataprotection")} <span className="text-red-500">*</span></span>
              </label>
              {errors.agree_dataprotection && <p className="text-red-500 text-sm">{errors.agree_dataprotection}</p>}

              <label className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  name="agree_billing_terms"
                  checked={formData.agree_billing_terms}
                  onChange={handleChange}
                  className="mt-1"
                />
                <span>{t("agree_billing_terms")} <span className="text-red-500">*</span></span>
              </label>
              {errors.agree_billing_terms && <p className="text-red-500 text-sm">{errors.agree_billing_terms}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderInput("location_of_signing", t("location_of_signing"), "text", "", true)}
              {renderInput("date_of_signing", t("date_of_signing"), "date", "", true)}
              {renderInput("signed_by", t("signed_by"), "text", "", true)}
            </div>
          </div>
        )}
        {IS_PRODUCTION && (
          <div className="my-4">
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => {
                setCaptchaToken(token);
                setCaptchaError("");
              }}
              onExpired={() => setCaptchaToken(null)}
            />
            {captchaError && <p className="text-red-500 text-sm mt-1">{captchaError}</p>}
          </div>
        )}

        <button
          type="submit"
          className="w-full bg-blue-600 text-white font-semibold py-3 rounded-md hover:bg-blue-700 transition"
        >
          {t("register")}
        </button>
      </form>
    </div>
  );
};

export default RegisterForm;